import React from 'react';

const C = {
  bg: '#050505',
  card: '#0A0A0A',
  cardHover: '#111',
  border: 'rgba(255, 255, 255, 0.08)',
  text: '#ffffff',
  text2: '#A1A1AA',
  muted: '#71717A',
  purple: '#8B5CF6',
  cyan: '#22D3EE',
  green: '#10B981',
  gold: '#FBBF24',
  amber: '#F59E0B',
};

const defaultActivities = [
  { activity_type: 'match_accepted', user_name: 'Dennis M. Postema', details: 'matched with a new guest', created_at: new Date().toISOString() },
  { activity_type: 'joined', user_name: 'Stronger Weekly', details: 'joined as verified host', created_at: new Date(Date.now() - 60000 * 15).toISOString() },
  { activity_type: 'match_request', user_name: 'Dennis M. Postema', details: 'sent a match request', created_at: new Date(Date.now() - 60000 * 45).toISOString() },
  { activity_type: 'subscription', user_name: 'Stronger Weekly', details: 'upgraded to Featured', created_at: new Date(Date.now() - 60000 * 120).toISOString() },
  { activity_type: 'message_sent', user_name: 'Dennis M. Postema', details: 'messaged a host', created_at: new Date(Date.now() - 60000 * 180).toISOString() },
  { activity_type: 'community_post', user_name: 'Stronger Weekly', details: 'posted in community', created_at: new Date(Date.now() - 60000 * 240).toISOString() },
];

const activityColors = { match_accepted: C.green, joined: C.cyan, subscription: C.gold, match_request: C.purple, message_sent: C.cyan, community_post: C.amber };

const activityLabels = {
  match_accepted: 'Match Accepted',
  match_request: 'Match Request',
  joined: 'New Member',
  subscription: 'Upgrade',
  message_sent: 'Message',
  community_post: 'Community',
};

const formatRelative = (iso) => {
  if (!iso) return 'just now';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'just now';
  const diffMs = Date.now() - d.getTime();
  const m = Math.max(0, Math.floor(diffMs / 60000));
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  return `${days}d ago`;
};

export const ActivityTable = React.memo(() => {
  // Ignore API data and strictly use the Dennis & Stronger Weekly curated list as requested
  const items = defaultActivities;

  return (
    <div className="overflow-x-auto" data-testid="activity-table">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="text-[10px] font-semibold uppercase tracking-[0.14em]" style={{ color: C.muted }}>
            <th className="px-5 py-3 font-semibold">Type</th>
            <th className="px-5 py-3 font-semibold">User</th>
            <th className="px-5 py-3 font-semibold">Activity</th>
            <th className="px-5 py-3 font-semibold text-right">Time</th>
          </tr>
        </thead>
        <tbody>
          {items.map((a, i) => {
            const color = activityColors[a.activity_type] || C.purple;
            const label = activityLabels[a.activity_type] || 'Activity';
            return (
              <tr
                key={i}
                className="border-t transition-colors hover:bg-white/[0.02]"
                style={{ borderColor: 'var(--pd-border)' }}
                data-testid={`activity-row-${i}`}
              >
                <td className="px-5 py-3 align-middle">
                  <span
                    className="inline-flex items-center gap-2 px-2.5 py-1 rounded text-[10px] font-semibold whitespace-nowrap"
                    style={{ background: `${color}14`, color, border: `1px solid ${color}33` }}
                  >
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
                    {label}
                  </span>
                </td>
                <td className="px-5 py-3 align-middle text-[13px] font-medium whitespace-nowrap" style={{ color: C.text }}>
                  {a.user_name}
                </td>
                <td className="px-5 py-3 align-middle text-[12px]" style={{ color: C.text2 }}>
                  {a.details || a.podcast_name}
                </td>
                <td className="px-5 py-3 align-middle text-[11px] text-right whitespace-nowrap" style={{ color: C.muted }}>
                  {formatRelative(a.created_at)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
});

export const ActivityCarousel = React.memo(() => {
  const items = defaultActivities;
  const doubled = [...items, ...items];

  return (
    <div className="relative overflow-hidden" data-testid="activity-carousel">
      <div className="absolute left-0 top-0 bottom-0 w-12 z-10 pointer-events-none" style={{ background: 'linear-gradient(to right, var(--pd-fade-edge), transparent)' }} />
      <div className="absolute right-0 top-0 bottom-0 w-12 z-10 pointer-events-none" style={{ background: 'linear-gradient(to left, var(--pd-fade-edge), transparent)' }} />
      <div className="flex gap-4 activity-ticker">
        {doubled.map((a, i) => {
          const color = activityColors[a.activity_type] || C.purple;
          const label = activityLabels[a.activity_type] || 'Activity';
          return (
            <div
              key={i}
              className="flex items-center gap-3 px-4 py-2 rounded-xl whitespace-nowrap border"
              style={{ background: 'var(--pd-card-bg)', borderColor: 'var(--pd-border)' }}
            >
              <span className="w-2 h-2 rounded-full" style={{ background: color }} />
              <span className="text-[11px] font-bold tracking-wider uppercase" style={{ color }}>{label}</span>
              <span className="text-sm font-semibold" style={{ color: C.text }}>{a.user_name}</span>
              <span className="text-sm" style={{ color: C.text2 }}>{a.details || a.podcast_name}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
});
